/**
 * Timezone utilities for detecting and formatting user's local timezone
 */

/**
 * Get the user's IANA timezone from browser
 * @returns IANA timezone string (e.g., "Australia/Melbourne", "America/New_York")
 */
export const getUserTimezone = (): string => {
  try {
    // Use Intl API to detect browser's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Get timezone abbreviation (e.g., "AEDT", "EST", "PST")
 * @param timezone IANA timezone string
 * @returns Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone?: string): string => {
  try {
    const tz = timezone || getUserTimezone();
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Get timezone offset in hours (e.g., +10, -5)
 * @param timezone IANA timezone string
 * @returns Offset in hours
 */
export const getTimezoneOffset = (timezone?: string): number => {
  try {
    const tz = timezone || getUserTimezone();
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
    return offset;
  } catch (error) {
    return 0;
  }
};

/**
 * Format timezone display string (e.g., "Australia/Melbourne (AEDT, UTC+11)")
 * @param timezone IANA timezone string
 * @returns Formatted timezone display string
 */
export const formatTimezoneDisplay = (timezone?: string): string => {
  try {
    const tz = timezone || getUserTimezone();
    const abbr = getTimezoneAbbreviation(tz);
    const offset = getTimezoneOffset(tz);
    const offsetSign = offset >= 0 ? '+' : '';
    return `${tz} (${abbr}, UTC${offsetSign}${offset})`;
  } catch (error) {
    return 'UTC';
  }
};
